<template>
  <div class="m-login__signin flipInX animated">
    <div class="m-login__head">
      <h3 class="m-login__title">Rejestracja</h3>
      <div class="m-login__desc">Wypełnij formularz, aby się zarejestrować.</div>
    </div>
    <form class="m-login__form m-form mt-4" @submit.prevent>
      <b-alert dismissible
               fade
               :show="isError"
               @dismissed="clearError"
               variant="danger">
        {{errorMessage}}
        <template slot="dismiss" class="pull-right">
          &nbsp;
        </template>
      </b-alert>
      <card-number-input v-if="allowEnteringNumber || registration.allowExistingNumbers"
                         ref="numberInput" class="form-group m-form__group"
                         v-model="number"
                         :required="allowEnteringNumber || !registration.allowExistingNumbers"
                         @valid="state.number = true" @invalid="state.number = false" />
      <div class="form-group m-form__group">
        <b-form-input :state="state.email"
                      type="text"
                      class="form-control m-input"
                      placeholder="E-mail" name="email" id="m_email"
                      autocomplete="off"
                      aria-describedby="emailFeedback"
                      v-model="email"></b-form-input>
        <b-form-invalid-feedback id="emailFeedback">
          Musisz podać e-mail
        </b-form-invalid-feedback>
      </div>
      <div class="form-group m-form__group">
        <password-input v-model="password"
                        placeholder="Hasło"
                        m-input
                        @valid="passwordState = true"
                        @invalid="passwordState = false"></password-input>

        <div v-if="state.password === false" class="invalid-feedback-custom">
          Musisz podać hasło
        </div>
      </div>

      <div class="form-group m-form__group">
        <b-form-input id="repeat-password"
                      :state="repeatPasswordState"
                      type="password"
                      class="m-input"
                      autocomplete="repeat-password"
                      placeholder="Powtórz hasło"
                      aria-describedby="repeatPasswordFeedback"
                      v-model="repeatPassword"></b-form-input>
        <b-form-invalid-feedback id="repeatPasswordFeedback">
          Podane hasła nie są identyczne
        </b-form-invalid-feedback>
        <div v-if="state.repeatPassword === false" class="invalid-feedback-custom">
          Musisz powtórzyć hasło
        </div>
      </div>

      <div class="m--margin-top-30">
        <div class="form-group m-form__group row m--margin-top-10">
          <label class="col-2 label-switch">
            <switches v-model="requiredAgreement"
                      v-b-tooltip.hover.left
                      @input="$root.$emit('bv::hide::tooltip')"
                      :title="requiredAgreement ? 'Zgoda udzielona' : 'Brak zgody'"
                      :color="requiredAgreement ? 'green' : 'default'"></switches>
          </label>
          <div class="col-10">
            <span v-if="state.agreement === false" class="small text-danger">
              Musisz zaznaczyć zgodę
            </span>
            <div class="text-justify">
              <required-agreement></required-agreement>
            </div>
          </div>
        </div>

        <div v-for="agreement in agreements" :key="agreement.number"
             class="form-group m-form__group row m--margin-top-10">
          <label class="col-2 label-switch">
            <switches v-model="agreement.enabled"
                      v-b-tooltip.hover.left
                      @input="onSwitchInput(agreement.number)"
                      :title="agreement.enabled ? 'Zgoda udzielona' : 'Brak zgody'"
                      :color="agreement.enabled ? 'green' : 'default'"></switches>
          </label>
          <div class="col-10">
            <span v-if="agreement.required === '1' && state.additionalAgreement[agreement.number] === false" class="small text-danger">
              Musisz zaznaczyć zgodę
            </span>
            <agreement-text :agreement="agreement"/>
          </div>
        </div>
      </div>
      <div class="m-login__form-action mb-3">
        <button id="m_login_forget_password_submit"
                class="btn m-btn m-btn--pill m-btn--custom m-btn--air btn-ls"
                :disabled="disableSubmit"
                @click="signUp">
          Wyślij
        </button>
        <button id="m_login_forget_password_cancel"
                class="btn m-btn m-btn--pill m-btn--custom btn-outline-ls m-btn--hover-ls mb-2"
                @click="$router.go(-1)">Anuluj
        </button>
      </div>
    </form>
  </div>
</template>

<script>
  import {mapActions, mapState} from 'vuex'
  import debounce from 'lodash.debounce'
  import Switches from 'vue-switches';
  import PasswordInput from "../input/PasswordInput";
  import {SELF_REGISTRATION_ENTER_NUMBER} from '@/utils/SelfRegistrationTypes';
  import RequiredAgreement from '../agreements/RequiredAgreement';
  import EmailValidator from 'email-validator';
  import CardNumberInput from "@/components/input/CardNumberInput";
  import {removeReflinkFields, setReflinkFields} from "@/utils/ReflinkFields";
  import AgreementText from "@/components/agreements/AgreementText";

  export default {
    name: "SignUpForm",
    components: {
      AgreementText,
      CardNumberInput,
      RequiredAgreement,
      Switches,
      PasswordInput
    },
    data() {
      return {
        number: '',
        email: '',
        password: '',
        repeatPassword: '',
        requiredAgreement: false,
        agreements: [],
        agreementsCloned: false,
        error: '',
        passwordState: false,
        state: {
          number: null,
          email: null,
          agreement: null,
          password: null,
          repeatPassword: null,
          additionalAgreement: {},
        },
        numberErrorMessage: 'Musisz podać numer klienta',
        lastChangedAgreementNumber: null,
        formValidated: false,
      }
    },
    mounted() {
      if(!this.allowEnteringNumber)
      {
        if (this.clientsExceeded || this.clientsNumberExceeded || this.businessClientsExceeded) {
          this.setLoginError(this.businessClientsExceeded
            ? 'Limit uczestników programu lojalnościowego został osiągnięty. Rejestracja konta nie jest możliwa.'
            : 'Rejestracja konta w tym momencie nie jest możliwa. W celu uzyskania pomocy, skontaktuj się z organizatorem programu lojalnościowego.');
          this.setSignInForm();
        }
      }

      this.$store.subscribe((mutation) => {
        //wait with clone for settings
        if (mutation.type === 'SET_SETTINGS') {
          this.cloneAgreementsFormState();
        }
      })
    },
    methods: {
      ...mapActions([
        'setSetPasswordForm',
        'setSignInForm',
        'setLoginInfo',
        'setLoginError'
      ]),
      cloneAgreementsFormState() {
        this.agreements = JSON.parse(JSON.stringify(this.stateAgreements))

        this.agreements.forEach((agreement) => {
          if (agreement.required === "1") {
            this.state.additionalAgreement[agreement.number] = null;
          }
        });

        setTimeout(() => {
          this.agreementsCloned = true;
        }, 1)
      },
      validateForm() {
        if (this.allowEnteringNumber) {
          this.$refs.numberInput.validate();
        }

        if (!this.email.length || !EmailValidator.validate(this.email)) {
          this.state.email = false
        }

        if (!this.requiredAgreement) {
          this.state.agreement = false
        }

        if (!this.password.length) {
          this.state.password = false;
        }

        if (!this.repeatPassword.length) {
          this.state.repeatPassword = false;
        }

        this.agreements.forEach((agreement) => {
          if (agreement.required === "1") {
            this.state.additionalAgreement[agreement.number] = agreement.enabled;
          }
        });

        //reactivity issues
        this.$set(this.state, 'additionalAgreement', this.state.additionalAgreement);

        this.formValidated = true;
      },
      signUp: debounce(function () {
        this.validateForm();

        if (!this.isFormValid) {
          return;
        }

        let form = {
          entityUuid: this.entityUuid,
          email: this.email,
          password: this.password,
        };

        this.agreements.forEach((agreement) => {
          if (agreement.enabled) {
            form[`agreement${agreement.number}`] = true
          }
        });

        if (this.allowEnteringNumber || this.registration.allowExistingNumbers) {
          form.number = this.number
        }

        setReflinkFields(this.entityUuid, form);

        this.$http.post('register', form)
          .then((r) => {
            if (r.data.success) {
              this.setLoginInfo(r.data.message);
              this.setSignInForm();
              removeReflinkFields(this.entityUuid);
            } else if(r.data.close_register) {
              this.setLoginError(r.data.message);
              this.setSignInForm();
            }else {

              this.error = r.data.message
            }
          })
      }, 400),
      clearError() {
        this.error = '';
      },
      onSwitchInput(agreementNumber) {
        this.$root.$emit('bv::hide::tooltip');
        this.lastChangedAgreementNumber = agreementNumber;
      }
    },
    computed: {
      ...mapState({
        color: state => state.settings.color,
        isTermsPageSet: state => state.settings.termsPageUuid !== null,
        termsPageUuid: state => state.settings.termsPageUuid,
        clientsExceeded: state => state.settings.entity.clientsExceeded,
        businessClientsExceeded: state => state.settings.entity.businessClientsExceeded,
        clientsNumberExceeded: state => state.settings.entity.clientsNumberExceeded,
        allowEnteringNumber: state => state.settings.selfRegistration === SELF_REGISTRATION_ENTER_NUMBER,
        stateAgreements: state => state.auth.agreements,
        entityUuid: state => state.auth.entity.uuid,
        clientNumberLabel: state => state.settings.clientNumberLabel,
        registration: state => state.settings.registration,
      }),
      isError() {
        return !!this.error.length
      },
      errorMessage() {
        return this.error
      },
      repeatPasswordState() {
        if (!this.repeatPassword.length) {
          return null
        }

        return this.password === this.repeatPassword;
      },
      disableSubmit() {
        if (!this.formValidated) {
          return false;
        }

        return this.state.number === false
          || this.state.email === false
          || this.state.agreement === false
          || this.state.password === false
          || this.state.repeatPassword === false
          || this.additionalRequiredAgreementsGiven === false;
      },
      isFormValid() {
        return ((this.allowEnteringNumber && this.state.number === true) || !this.allowEnteringNumber)
          && this.state.email === true
          && this.state.agreement === true
          && this.state.password === true
          && this.state.repeatPassword === true
          && this.passwordState === true
          && this.password === this.repeatPassword
          && this.additionalRequiredAgreementsGiven === true;
      },
      additionalRequiredAgreementsGiven() {
        return this.agreements
            .filter(agreement => agreement.required === '1')
            .map(agreement => agreement.enabled)
            .reduce((accumulator, agreement) => accumulator && agreement, true);
      }
    },
    watch: {
      email(v) {
        if (!this.state.email && v.length) {
          this.state.email = true
        }
      },
      requiredAgreement(v) {
        this.state.agreement = v
      },
      agreements: {
        deep: true,
        handler: function (val) {
          if (!this.agreementsCloned) {
            return;
          }

          val.forEach((agreement) => {
            if (agreement.number !== this.lastChangedAgreementNumber) {
              return;
            }

            if (agreement.required === '1') {
              this.state.additionalAgreement[agreement.number] = agreement.enabled;
            }
          })

          //reactivity issues
          this.$set(this.state, 'additionalAgreement', this.state.additionalAgreement);
        }
      },
      password(v, o) {
        if (!o.length && !this.state.password && v.length) {
          this.state.password = true
        }
      },
      repeatPassword(v, o) {
        if (!o.length && !this.state.repeatPassword && v.length) {
          this.state.repeatPassword = true
        }
      },
    }
  }
</script>

<style scoped>
  .m-link {
    color: var(--accent-color) !important;
    cursor: pointer;
  }

  .invalid-feedback-custom {
    width: 100%;
    margin-top: .25rem;
    font-size: 80%;
    color: #f4516c;
  }

</style>
